import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Slide,
  Button,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  KeyboardArrowRight,
  KeyboardArrowLeft,
  Keyboard,
  VolumeUp,
  QuestionAnswer,
  GridView,
  Close,
  Fullscreen,
  TouchApp
} from "@mui/icons-material";
import { styled } from '@mui/material/styles';

// Styled components for enhanced visual design
const StyledPaper = styled(Paper)(({ theme }) => ({
  position: "fixed",
  right: 0,
  top: 0,
  bottom: 0,
  width: "350px",
  zIndex: 1000,
  overflowY: "auto",
  backgroundColor: "rgb(248, 245, 255)",
  borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
  boxShadow: "0 0 20px rgba(0, 0, 0, 0.15)",
  [theme.breakpoints.down('sm')]: {
    width: "100%",
  }
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px 24px",
  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  background: "linear-gradient(to right, #4527a0, #7b1fa2)",
  color: "white"
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: "8px",
  margin: "8px 0",
  transition: "all 0.2s ease",
  '&:hover': {
    backgroundColor: "rgba(33, 150, 243, 0.08)"
  }
}));

const KeyShortcutBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  padding: "10px 16px",
  borderRadius: "6px",
  backgroundColor: "rgba(33, 150, 243, 0.06)",
  border: "1px solid rgba(33, 150, 243, 0.1)"
}));

const KeyHighlight = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 10px",
  borderRadius: "4px",
  backgroundColor: "white",
  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  margin: "0 4px",
  fontWeight: 600,
  fontSize: "0.85rem",
  border: "1px solid rgba(0,0,0,0.1)",
  color: "#4527a0"
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "20px",
  textTransform: "none",
  background: "linear-gradient(to right, #4527a0, #7b1fa2)",
  padding: "8px 24px",
  boxShadow: "0 4px 10px rgba(123, 31, 162, 0.25)",
  '&:hover': {
    boxShadow: "0 6px 12px rgba(123, 31, 162, 0.35)",
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: "#4527a0",
  marginTop: "20px",
  marginBottom: "10px",
  paddingLeft: "16px"
}));

const CloseIconButton = styled(IconButton)(({ theme }) => ({
  color: "white",
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'scale(1.1)',
    transition: 'all 0.2s ease',
  },
  transition: 'all 0.2s ease',
}));

const CardNavigationHelpGuide = ({ open, onClose }) => {
  const [showContent, setShowContent] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Effect to handle animation delay
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [open]);

  // Effect to handle Escape key press
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit sx={{mt:6}}>
      <StyledPaper elevation={5}>
        <HeaderBox>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Keyboard Navigation Guide
          </Typography>
          <CloseIconButton onClick={onClose} size="small" aria-label="close">
            <Close />
          </CloseIconButton>
        </HeaderBox>

        <Slide direction="left" in={showContent} timeout={300}>
          <Box sx={{ p: 3 }}>
            <SectionTitle variant="h6">Card Navigation</SectionTitle>
            
            <StyledListItem>
              <ListItemIcon>
                <Keyboard sx={{ color: "#4527a0" }} />
              </ListItemIcon>
              <ListItemText
                primary="Navigate Within Cards"
                secondary="Move between steps inside each learning card"
                primaryTypographyProps={{ fontWeight: 600, color: "#333" }}
                secondaryTypographyProps={{ color: "#666" }}
              />
            </StyledListItem>

            <Box sx={{ pl: 4, pr: 2 }}>
              <KeyShortcutBox>
                <KeyboardArrowRight sx={{ color: "#2196f3", mr: 1 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Press <KeyHighlight>→</KeyHighlight> for next step
                </Typography>
              </KeyShortcutBox>
              
              <KeyShortcutBox>
                <KeyboardArrowLeft sx={{ color: "#2196f3", mr: 1 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Press <KeyHighlight>←</KeyHighlight> for previous step
                </Typography>
              </KeyShortcutBox>
            </Box>

            <Divider sx={{ my: 3, opacity: 0.6 }} />

            <SectionTitle variant="h6">Card to Card Navigation</SectionTitle>

            <StyledListItem>
              <ListItemIcon>
                <Fullscreen sx={{ color: "#4527a0" }} />
              </ListItemIcon>
              <ListItemText
                primary="Move Between Cards"
                secondary="Skip to the next or previous card in your learning session"
                primaryTypographyProps={{ fontWeight: 600, color: "#333" }}
                secondaryTypographyProps={{ color: "#666" }}
              />
            </StyledListItem>

            <Box sx={{ pl: 4, pr: 2 }}>
              <KeyShortcutBox>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Press <KeyHighlight>Shift</KeyHighlight> + <KeyHighlight>→</KeyHighlight> for next card
                </Typography>
              </KeyShortcutBox>
              
              <KeyShortcutBox>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Press <KeyHighlight>Shift</KeyHighlight> + <KeyHighlight>←</KeyHighlight> for previous card
                </Typography>
              </KeyShortcutBox>
            </Box>

            <Divider sx={{ my: 3, opacity: 0.6 }} />

            <StyledListItem>
              <ListItemIcon>
                <VolumeUp sx={{ color: "#4527a0" }} />
              </ListItemIcon>
              <ListItemText
                primary="Audio Playback"
                secondary="Listen to pronunciation with a single key"
                primaryTypographyProps={{ fontWeight: 600, color: "#333" }}
                secondaryTypographyProps={{ color: "#666" }}
              />
            </StyledListItem>

            <Box sx={{ pl: 4, pr: 2 }}>
              <KeyShortcutBox>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Press <KeyHighlight>P</KeyHighlight> to play audio
                </Typography>
              </KeyShortcutBox>
            </Box>

            <Divider sx={{ my: 3, opacity: 0.6 }} />

            <StyledListItem>
              <ListItemIcon>
                <TouchApp sx={{ color: "#4527a0" }} />
              </ListItemIcon>
              <ListItemText
                primary="Touch Navigation"
                secondary="For mobile and touch devices"
                primaryTypographyProps={{ fontWeight: 600, color: "#333" }}
                secondaryTypographyProps={{ color: "#666" }}
              />
            </StyledListItem>

            <Box sx={{ pl: 4, pr: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                Use the arrow buttons at the sides of the card for navigation, or tap the card number indicators at the bottom.
              </Typography>
            </Box>

            <Divider sx={{ my: 3, opacity: 0.6 }} />

            <StyledListItem>
              <ListItemIcon>
                <QuestionAnswer sx={{ color: "#4527a0" }} />
              </ListItemIcon>
              <ListItemText
                primary="Review Mode"
                secondary="After completing all cards, test your knowledge in review mode"
                primaryTypographyProps={{ fontWeight: 600, color: "#333" }}
                secondaryTypographyProps={{ color: "#666" }}
              />
            </StyledListItem>

            <KeyShortcutBox sx={{ ml: 4, mr: 2, mb: 3 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                Press <KeyHighlight>H</KeyHighlight> to toggle this help guide
              </Typography>
            </KeyShortcutBox>

            <Box sx={{ mt: 4, textAlign: "center" }}>
              <StyledButton
                variant="contained"
                color="primary"
                onClick={onClose}
              >
                Got it!
              </StyledButton>
            </Box>
          </Box>
        </Slide>
      </StyledPaper>
    </Slide>
  );
};

export default CardNavigationHelpGuide;